/**
 * Authentication API Module
 * Handles session creation, validation, refresh, and logout
 */

import axiosClient, { isNoAuthMode } from "./client";
import {
  SessionResponse,
  RefreshSessionResponse,
  UserProfileResponse,
} from "./types";
import { getSessionId, getAuthType } from "../security/authStorage";
import { getHostUri, isAuthPassEnabled } from "../security/authHelpers";
import { idpLogin } from "@uhg-optum-coreplatform/security-as-a-service-pkg";

// ============================================================================
// Session Management
// ============================================================================

/**
 * Create a new session from OAuth code
 *
 * When `authpassEnabled` is true, sessions are created via the auth-proxy
 * `/loginap` endpoint. Otherwise, falls back to the legacy `./session`
 * endpoint used before the auth-proxy was introduced.
 */
export async function createSession(
  code: string,
  state?: string,
): Promise<SessionResponse> {
  const authType = getAuthType();

  if (isAuthPassEnabled()) {
    const params = new URLSearchParams({ code, authType });
    if (state) {
      params.set('state', state);
    }
    const response = await axiosClient.get<SessionResponse>(
      `/loginap?${params.toString()}`,
    );
    return response.data;
  }

  const response = await axiosClient.post<SessionResponse>(
    `./session?redirect_uri=${getHostUri() + "/"}&auth_type=${authType}`,
    { code },
  );
  return response.data;
}

/**
 * Validate current session
 */
export async function validateSession(tenantId: string): Promise<boolean> {
  // No-auth mode always returns true
  if (isNoAuthMode()) {
    const response = await axiosClient.get("./local/session.json");
    return response.data;
  }

  const response = await axiosClient.get<boolean>("./secure/validate-session", {
    headers: {
      'session-id': getSessionId() as string,
      'modulename': tenantId,
      'authType': getAuthType() as string,
    },
  });
  return response.data;
}

/**
 * Refresh session (extends session timeout)
 */
export async function refreshSession(): Promise<RefreshSessionResponse> {
  const response = await axiosClient.get<RefreshSessionResponse>(
    "./secure/refresh",
    {
      headers: {
        'session-id': getSessionId() as string,
        'auth-type': getAuthType() as string,
      },
    }
  );
  return response.data;
}

/**
 * Logout user and clear server-side session
 * Backend clears session storage; frontend handles IdP logout separately if needed
 */
export async function logoutUser(): Promise<void> {
  await axiosClient.get('./secure/logout');
}

// ============================================================================
// User Profile
// ============================================================================

/**
 * Get user profile information
 *
 * When `authpassEnabled` is true, the profile is fetched from the auth-proxy
 * `/secure/user-profile-id` endpoint using the session-id header. Otherwise,
 * falls back to the legacy `./secure/user-profile` endpoint used before the
 * auth-proxy was introduced.
 */
export async function getUserProfile(): Promise<UserProfileResponse> {
  // No-auth mode loads from local file
  if (isNoAuthMode()) {
    const response = await axiosClient.get<UserProfileResponse>(
      "./local/userProfile.json",
    );
    return response.data;
  }

  if (isAuthPassEnabled()) {
    const response = await axiosClient.get<UserProfileResponse>(
      `./secure/user-profile-id`,
      { headers: { 'session-id': getSessionId() as string } },
    );
    return response.data;
  }

  const response = await axiosClient.get<UserProfileResponse>(
    `./secure/user-profile?session-id=${getSessionId()}`,
  );
  return response.data;
}

// ============================================================================
// Login Redirect
// ============================================================================

/**
 * Redirect to login page
 *
 * When `authpassEnabled` is true, delegates to the IdP login flow via the
 * security-as-a-service package. Otherwise, falls back to the legacy
 * `./login` redirect used before the auth-proxy was introduced.
 */
export function redirectToLogin(authType: string): void {
  if (isAuthPassEnabled()) {
    const env = (window as any)["environments"];
    let settings;
    if (authType === "msid") {
      settings = {
        clientId: env.pfClientId,
        redirectUri: env.redirectUri,
        responseType: "code",
        scope: "openid profile address email phone",
        action: env.pfAction,
        extraAuthParameters: { acr_values: "R1_AAL1_MS-AD-Kerberos" },
      };
    } else {
      settings = {
        clientId: env.ohidClientId,
        redirectUri: env.redirectUri,
        responseType: "code",
        scope: "openid profile address email phone",
        action: env.ohidAction,
      };
    }

    idpLogin(settings);
    return;
  }

  // Fallback to call the redirect to the legacy login endpoint if authpass is not enabled
  window.location.href = `./login?redirect_uri=${getHostUri() + "/"}&auth_type=${getAuthType()}`;
}
